import type { components } from '../../api/generated/schema'
import { parseMoney } from '../../api/parseMoney'

/**
 * The only place a `GET /api/v1/insights` body is read. Everything past this
 * module sees `Insights`: series money in integer cents, the server's sentences
 * untouched. The server orders insights most severe first; nothing here
 * re-sorts. The current month is the LAST series point, as the wire says.
 *
 * An unknown `kind` or `severity` is dropped, not thrown: a new insight type or
 * level must not need a frontend release. Any other shape problem is ONE `InsightsContractError`
 * naming the field.
 */

export type InsightSeverity = 'info' | 'warn' | 'alert'

export type Insight = {
  id: string
  kind: 'category_anomaly'
  severity: InsightSeverity
  title: string
  body: string
  why: string
  categoryId: string
  /** twelve months, oldest first; the last point is the current month */
  series: { month: string; amountCents: number }[]
}

export type Insights = { currency: string; insights: Insight[] }

/** The generated wire shape. `figures`, `driver` and `period_*` are documented but unread. */
export type InsightsWire = components['schemas']['InsightsResponse']

export class InsightsContractError extends Error {
  readonly path: string
  constructor(path: string, message: string) {
    super(`Insights response: ${path}: ${message}`)
    this.name = 'InsightsContractError'
    this.path = path
  }
}

type Rec = Record<string, unknown>

function readRec(v: unknown, path: string): Rec {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) {
    throw new InsightsContractError(path, 'expected an object')
  }
  return v as Rec
}

function readString(v: unknown, path: string): string {
  if (typeof v !== 'string') throw new InsightsContractError(path, 'expected a string')
  return v
}

function readMoney(v: unknown, path: string): number {
  const s = readString(v, path)
  try {
    return parseMoney(s)
  } catch {
    throw new InsightsContractError(path, 'not a money string')
  }
}

function isSeverity(v: unknown): v is InsightSeverity {
  return v === 'info' || v === 'warn' || v === 'alert'
}

function readMonth(v: unknown, path: string): string {
  const s = readString(v, path)
  if (!/^\d{4}-\d{2}$/.test(s)) throw new InsightsContractError(path, 'not a YYYY-MM month')
  return s
}

function readSeries(v: unknown, path: string): Insight['series'] {
  if (!Array.isArray(v) || v.length === 0) {
    throw new InsightsContractError(path, 'expected a non-empty list')
  }
  return v.map((row, i) => {
    const r = readRec(row, `${path}[${i}]`)
    return {
      month: readMonth(r['month'], `${path}[${i}].month`),
      amountCents: readMoney(r['amount'], `${path}[${i}].amount`),
    }
  })
}

export function toInsights(body: unknown): Insights {
  const r = readRec(body, 'body')
  const list = r['insights']
  if (!Array.isArray(list)) throw new InsightsContractError('insights', 'expected a list')
  const insights: Insight[] = []
  list.forEach((row, i) => {
    const p = `insights[${i}]`
    const o = readRec(row, p)
    if (o['kind'] !== 'category_anomaly') return
    const severity = o['severity']
    if (!isSeverity(severity)) return
    insights.push({
      id: readString(o['id'], `${p}.id`),
      kind: 'category_anomaly',
      severity,
      title: readString(o['title'], `${p}.title`),
      body: readString(o['body'], `${p}.body`),
      why: readString(o['why'], `${p}.why`),
      categoryId: readString(o['category_id'], `${p}.category_id`),
      series: readSeries(o['series'], `${p}.series`),
    })
  })
  return { currency: readString(r['currency'], 'currency'), insights }
}
